import type { Table } from '@tanstack/react-table';
import type { TableV2FilteringProps } from '../types';

/**
 * What the "No results match your filters" row's Clear filters does, or `undefined` when the table
 * has no way to clear them and must not offer the button.
 *
 * The table resets only the state it owns. A column filter passed as `filteringState` is controlled:
 * the prop is merged over the table's own state on every render, so resetting the table's copy
 * changes nothing and a Clear filters button there would be a no-op. Those filters are cleared
 * through the caller, by `onClearFilters`, or by `onColumnFiltersChange` with an empty list. The
 * global filter is always the table's own and is cleared with them.
 */
export function getClearFiltersAction<T, ColumnId extends string = string>(
  filtering: Pick<TableV2FilteringProps<ColumnId>, 'onClearFilters' | 'onColumnFiltersChange'> | undefined,
  table: Pick<Table<T>, 'resetColumnFilters' | 'setGlobalFilter'>,
): (() => void) | undefined {
  if (filtering === undefined) {
    return () => {
      table.resetColumnFilters(true);
      table.setGlobalFilter('');
    };
  }
  const { onClearFilters, onColumnFiltersChange } = filtering;
  if (onClearFilters) {
    return () => {
      onClearFilters();
      table.setGlobalFilter('');
    };
  }
  if (onColumnFiltersChange) {
    return () => {
      onColumnFiltersChange([]);
      table.setGlobalFilter('');
    };
  }
  return undefined;
}
